/**
 * @file Жизненный цикл подключения на странице хранилища.
 *
 * Смена хранилища/токена отменяет предыдущий цикл. Проверки здоровья идут
 * последовательно: новый таймер начинается после окончания запроса.
 * При ошибке соединение закрывается для UI до явного повтора пользователем.
 */
import { useEffect, useState } from 'react'

import { ensureTrafficQuota } from '../features/traffic/api.ts'
import { abortableDelay } from '../services/abort.ts'
import { checkTunnelHealth, connectTunnel } from '../services/tunnel.ts'
import type { TunnelConnectionPhase } from '../services/tunnel.ts'

type Params = { boxId?: string; token?: string }
type ConnectionPhase = 'quota' | TunnelConnectionPhase
type ConnectionStatus = ConnectionPhase | 'connected' | 'error'

/** Подготовка страницы: квота, затем туннель. Отдельная функция для unit-тестов. */
export async function prepareStorageConnection(
    boxId: string,
    token: string,
    signal: AbortSignal,
    {
        quotaTimeoutMs = 30_000,
        onPhase = () => {},
    }: {
        quotaTimeoutMs?: number
        onPhase?: (phase: ConnectionPhase) => void
    } = {},
): Promise<void> {
    signal.throwIfAborted()
    onPhase('quota')
    try {
        await ensureTrafficQuota(
            boxId,
            AbortSignal.any([signal, AbortSignal.timeout(quotaTimeoutMs)]),
        )
    } catch {
        // Ошибка или таймаут квоты не блокируют туннель. Отмену всей
        // подготовки проверяем отдельно, чтобы не продолжить после ухода.
    }
    signal.throwIfAborted()

    // У туннеля свой срок ожидания: время запроса квоты его не сокращает.
    await connectTunnel(boxId, token, signal, { onPhase })
}

export function useTunnelConnection({ boxId, token }: Params) {
    const [state, setState] = useState<{
        boxId: string
        token: string
        status: ConnectionStatus
    } | null>(null)
    const [revision, setRevision] = useState(0)

    useEffect(() => {
        if (!boxId || !token) return

        const target = { boxId, token }
        const controller = new AbortController()
        const { signal } = controller
        setState({ ...target, status: 'quota' })

        async function run() {
            try {
                await prepareStorageConnection(
                    target.boxId,
                    target.token,
                    signal,
                    {
                        onPhase: (status) => {
                            if (!signal.aborted) setState({ ...target, status })
                        },
                    },
                )
                signal.throwIfAborted()
                setState({ ...target, status: 'connected' })

                // await не даёт двум healthcheck пересечься даже при медленной сети.
                while (true) {
                    await abortableDelay(15_000, signal)
                    await checkTunnelHealth(target.boxId, target.token, signal)
                }
            } catch {
                // Отмена старого эффекта не является ошибкой нового подключения.
                if (!signal.aborted) setState({ ...target, status: 'error' })
            }
        }

        void run()
        return () => controller.abort()
    }, [boxId, token, revision])

    // Уже в первом отображении нового хранилища не выдаём старое connected за новое.
    const status =
        state?.boxId === boxId && state?.token === token
            ? state?.status
            : 'quota'
    const available = Boolean(boxId && token)
    const phase = status === 'connected' || status === 'error' ? null : status

    return {
        phase,
        connecting: available && phase !== null,
        connected: available && status === 'connected',
        error: available && status === 'error',
        retry: () => setRevision((value) => value + 1),
    }
}
